import type { TaskCompletionPointDto } from "@/lib/tasks";

export interface TaskCompletionChartProps {
  series: TaskCompletionPointDto[];
}

// Fixed viewBox with inner padding so markers/strokes are not clipped at edges.
const VIEW_W = 280;
const VIEW_H = 80;
const PAD_X = 8;
const PAD_Y = 10;

export function TaskCompletionChart({ series }: TaskCompletionChartProps) {
  const total = series.reduce((sum, p) => sum + p.count, 0);

  if (total === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        No completions in the last 14 days.
      </p>
    );
  }

  const counts = series.map((p) => p.count);
  const min = Math.min(...counts);
  const max = Math.max(...counts);
  const span = max - min;

  const innerW = VIEW_W - PAD_X * 2;
  const innerH = VIEW_H - PAD_Y * 2;

  const points = series.map((p, index) => {
    const x =
      series.length === 1
        ? VIEW_W / 2
        : PAD_X + (innerW * index) / (series.length - 1);
    // Flat line at mid when all values equal/zero; otherwise invert (higher -> up).
    const norm = span === 0 ? 0.5 : (p.count - min) / span;
    const y = PAD_Y + innerH * (1 - norm);
    return { x, y, point: p, index };
  });

  const polyline = points.map((p) => `${p.x},${p.y}`).join(" ");

  return (
    <div
      role="img"
      aria-label={`Task completions over the last 14 days: ${total} total`}
    >
      <svg
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        className="w-full h-auto"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <polyline
          points={polyline}
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          className="text-primary"
        />
        {points.map((p) => (
          <circle
            key={p.point.date}
            cx={p.x}
            cy={p.y}
            r={1.5}
            fill="currentColor"
            className="text-primary"
          />
        ))}
      </svg>
    </div>
  );
}
